// supervisor-outcome-router-needs-decision-path.ts
//
// Nhánh `needs_decision` của supervisor-outcome-router.ts, tách ra theo đúng
// khuôn `*-done-path.ts` để router giữ ngân sách 200 dòng.
//
// B4: worker khai `needs_decision` ⇒ ghi câu hỏi interview, KHÔNG tính
// counted_failure — cần NGƯỜI quyết, không phải lỗi của worker.
//
// V2-0 (spec V2 §4.0): id câu hỏi theo nội dung. Worker hỏi lại ĐÚNG câu người
// đã trả lời (cùng hash ⇒ cùng id) ⇒ 1 counted failure `contract_error`, không
// đẻ câu mới: câu trả lời đã nằm trong prompt (mục "Quyết định đã chốt cho
// ticket này"), hỏi lại là worker bỏ qua hợp đồng chứ không phải cần người.
// Câu khác hash ⇒ câu required mới, KHÔNG tính failure.
// Category `contract_error` này cố ý KHÔNG vào breaker "≥ 2 ticket dừng run"
// (contract-error-circuit-breaker.ts header): lỗi một worker, không lỗi hệ thống.

import { readInterview, writeInterviewQuestions } from "./interview-store.js";
import { buildNeedsDecisionQuestion } from "./needs-decision-interview-questions.js";
import { isNeedsDecisionQuestionOfTicket } from "./needs-decision-question-ticket-match.js";
import {
  type RetryConfig,
  type RetryDecision,
  type RetryState,
  recordAttemptOutcome,
} from "./retry-policy.js";
import type { AttemptCycleResult } from "./supervisor-attempt-cycle.js";

export interface NeedsDecisionPathInput {
  cycle: AttemptCycleResult;
  controlRoot: string;
  ticketId: string;
  now: Date;
  retryState: RetryState;
  retryConfig: RetryConfig;
}

export type NeedsDecisionPathResult =
  | { kind: "needs_decision"; questionId: string }
  | { kind: "retry_decision"; decision: RetryDecision };

/**
 * `undefined` khi attempt không phải needs_decision (đường cũ nguyên vẹn).
 *
 * Ticket quay về "todo" qua nhánh `undefined` của `journalAttemptOutcome`
 * (supervisor-loop-outcome-journal.ts:147-158) và gate `interview_unanswered`
 * giữ dispatch kế tiếp — worker chạy đúng một lần rồi chờ người.
 */
export async function routeNeedsDecisionOutcome(
  input: NeedsDecisionPathInput,
): Promise<NeedsDecisionPathResult | undefined> {
  if (input.cycle.kind !== "verified") return undefined;
  const question = buildNeedsDecisionQuestion(input.cycle.sessionResult.result, input.ticketId);
  if (question === undefined) return undefined;

  const existing = await readInterview(input.controlRoot);
  if (existing?.answers[question.id] !== undefined) {
    return {
      kind: "retry_decision",
      decision: recordAttemptOutcome(
        input.retryState,
        {
          category: "contract_error",
          evidence:
            `needs_decision lặp đúng câu đã được người trả lời (${question.id}) — ` +
            'quyết định đã có trong mục "Quyết định đã chốt cho ticket này" của prompt.',
        },
        input.retryConfig,
        input.now,
      ),
    };
  }

  // writeInterviewQuestions ghi đè cả bộ, nên phải gộp thủ công: giữ câu của
  // ticket KHÁC và câu ĐÃ TRẢ LỜI của chính ticket này (lịch sử, không chặn
  // dispatch). Chỉ câu CHƯA trả lời của ticket này bị thay bằng câu mới (chốt
  // 06/10): nó đã lỗi thời (worker vừa hỏi câu khác), giữ lại sẽ chặn ticket
  // mãi bằng một câu không ai còn hỏi. Ca này gần như chỉ xảy ra khi gate đã
  // cho qua dù còn câu treo; directive answer cho câu bị thay (nếu còn trong
  // inbox) bị từ chối có lý do, không mất im lặng.
  const others = (existing?.questions ?? []).filter(
    (q) =>
      !isNeedsDecisionQuestionOfTicket(q, input.ticketId) || existing?.answers[q.id] !== undefined,
  );
  await writeInterviewQuestions({
    controlRoot: input.controlRoot,
    questions: [...others, question],
    now: () => input.now,
  });

  return { kind: "needs_decision", questionId: question.id };
}
